import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { fromEvent, Subscription } from 'rxjs';
import { filter, debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { TemplateDialogComponent } from 'app/views/site-review/template-dialog/template-dialog.component';
import { EditHistoryDialogComponent } from 'app/views/site-review/job-master/edit-history-dialog/edit-history-dialog.component';
import { Command } from 'app/models/command';
import { SpinnerComponent } from '@mycommon/spinner/spinner.component';
import { SiteReviewService } from 'app/services/site-review.service';
import { SearchMultipleDialogComponent } from './search-multiple-dialog/search-multiple-dialog.component';
import { AuthService } from 'app/services/auth/auth.service';
import { GridApi, GridOptions } from 'ag-grid-community';
import { IdbService } from 'app/services/idb.service';
import { WbExpandedSearch } from './wb-expanded-search/wb-expanded-search';
import { PgePmSearch } from 'app/views/site-review/pge-reporting/pge-pm-search/pge-pm-search';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FeatureFlagService } from 'app/core/feature-flags/feature-flags.service';
import { TemplateViewService } from 'app/services/template-view.service';
import { ManageViewsDialogComponent, ManageViewsDialogResult } from './manage-views-dialog/manage-views-dialog.component';

@Component({
  selector: 'app-grid-toolbar',
  templateUrl: './grid-toolbar.component.html',
  styleUrls: ['./grid-toolbar.component.scss'],
  standalone: false,
})
export class GridToolbarComponent implements AfterViewInit, OnDestroy {
  
  @Input() pageCode: string;
  @Input() showMultipleSearch: boolean = false;
  @Input() selectWeekOption: boolean = false;
  @Input() disableWeekSelector: boolean = false;
  @Input() showHistoryButton: boolean = false;
  @Input() showCrewSelector: boolean = false;
  @Input() showTemplateSelector: boolean = true;
  @Input() showSearchInput: boolean = true;
  // Opt-in explicit Search button beside the search field. Off by default so the
  // existing screens keep the type-to-filter-only behaviour.
  @Input() showSearchButton: boolean = false;
  @Input() showWBExpandedSearch: boolean = false;
  @Input() searchByPMs: boolean = false;
  @Input() selectDateRangeOption: boolean = false;
  @Input() lockStartDate: boolean = false;
  @Input() gridOptions: GridOptions;
  @Input() initialDateRange: { startDate: string, endDate: string } | null = null;
  // Opt-in for hosts that restore their own grid state (filters, sort, search)
  // when the user returns to the page. On first load the toolbar then keeps
  // that state instead of resetting the grid or re-applying the active view.
  @Input() skipInitialGridReset: boolean = false;
  // Pre-fills the search box to match a quickFilterText the host restored.
  @Input() initialSearchText: string | null = null;
  // Week to select on load instead of the current one (reportingWeekID).
  @Input() initialWeekNumber: number | null = null;

  @Input() set gridApi(api: GridApi) {
    this._gridApi = api;
    if (api && this.gridOptions) {
    }
  }
  get gridApi(): GridApi {
    return this._gridApi;
  }
  private _gridApi: GridApi;

  @Output() selectedWeek = new EventEmitter<void>();
  @Output() selectedDateRange = new EventEmitter<any>();
  @Output() selectedWeekWithForeman = new EventEmitter<any>();
  
  @ViewChild('searchbox') searchbox!: ElementRef<HTMLInputElement>;
  
  activeTemplateViewID: number = 0;
  searchByOption = '0';
  reportWeeks: any;
  weekNumber: any;
  dateOptions: any;
  currentWeek: any;
  showInputSearch: boolean = false;
  clearFilter: boolean = false;
  sysadmin: boolean = false;
  templates: any[] = [];
  foreManList: any;
  gridState = { 
    columnState: null, 
    filterState: null, 
    sortState: null, 
    groupState: null,
    valueState: null  
  };
  searchObj: { input: any ; searchBy: string; };
  showEditTemplateButton: boolean = false;
  sharedTemplates: any[] = [];
  privateTemplates: any[] = [];
  // Manage Views (MANAGE_VIEWS feature flag) — new-style dropdown grouping:
  // Global → My Views → shared (public + shared-with-me merged; to the user
  // they're the same thing)
  manageViewsEnabled: boolean = false;
  globalTemplates: any[] = [];
  myViewTemplates: any[] = [];
  sharedViewTemplates: any[] = [];
  week: any;
  employeeID: number;

  showSaveTemplateButton: boolean = false; 
  private initialGridState: any; 
  loadingTemplates: boolean = true;

  startDateRange: Date;
  endDateRange: Date;

  bkGridData: any;
  expandedSearchObj: any = {};
  clearExpandedSearchBtn: boolean = false;

  private searchSubscription?: Subscription;

  constructor(private dialog: MatDialog,
              private authService: AuthService,
              private idb: IdbService,
              private snackBar: MatSnackBar,
              private siteRevService: SiteReviewService,
              private featureFlags: FeatureFlagService,
              private templateViews: TemplateViewService) {}


  ngAfterViewInit() {
    this.manageViewsEnabled = this.featureFlags.isEnabled('MANAGE_VIEWS');
    this.getTemplates();
    this.checkIfSysAdmin();

    if (this.showSearchInput) {
      this.searchSubscription = fromEvent(this.searchbox.nativeElement, 'keyup')
        .pipe(
            filter(Boolean),
            debounceTime(150),
            distinctUntilChanged(),
            tap(() => {
                const searchText = this.searchbox.nativeElement.value;
                this.gridApi.setGridOption('quickFilterText', searchText);
                this.clearFilter = true;
            })
        )
        .subscribe();

      if (this.initialSearchText) {
        this.searchbox.nativeElement.value = this.initialSearchText;
        this.clearFilter = true;
      }
    }
    if (this.selectWeekOption) {
        this.timeSheetGetReportingWeek();
    }
    if (this.selectDateRangeOption) {
      if (this.initialDateRange) {
        this.startDateRange = new Date(this.initialDateRange.startDate);
        this.endDateRange = new Date(this.initialDateRange.endDate);
        setTimeout(() => {
          this.selectedDateRange.emit({
            startDate: this.initialDateRange.startDate,
            endDate: this.initialDateRange.endDate
          });
        }, 0);
      } else {
        this.setCurrentWeek();
      }
    }
    
    if (this.gridApi) {
        this.storeInitialGridState();
        this.setupGridEventListeners();
    }
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    // if (this.gridApi) {
    //   this.gridApi.destroy();
    // }
  }

  checkIfSysAdmin() {
    let groups: string[]
    groups = this.authService.currentUser.groups;
    this.sysadmin = groups.indexOf('System Management') > -1
  }

  private sortAndGroupTemplates() {
    const real = this.templates.filter(t => !/^-+$/.test((t.templateViewName || '').trim()));

    if (this.manageViewsEnabled) {
      // Effective visibility (tolerates legacy IDB-cached rows) — single
      // source of the fallback rule: TemplateViewService.visibilityOf
      const vis = (t: any) => this.templateViews.visibilityOf(t);
      const isOwn = (t: any) => t.isOwner === 1
        || (t.isOwner === undefined
            && t.createdByID === this.authService.session.internalContactsID);
      // Subscribed views only — but always keep the active one so its label renders
      const shown = (t: any) => t.subscribed === undefined || t.subscribed !== 0
        || t.templateViewID === this.activeTemplateViewID;
      const byName = (a: any, b: any) =>
        (a.templateViewName || '').localeCompare(b.templateViewName || '');

      this.globalTemplates = real
        .filter(t => vis(t) === 'Global')
        .sort((a, b) => (a.templateSortOrder ?? 0) - (b.templateSortOrder ?? 0) || byName(a, b));
      this.myViewTemplates = real
        .filter(t => vis(t) !== 'Global' && isOwn(t) && shown(t))
        .sort(byName);
      this.sharedViewTemplates = real
        .filter(t => vis(t) !== 'Global' && !isOwn(t) && shown(t))
        .sort(byName);
      return;
    }

    this.sharedTemplates = real
      .filter(t => t.publicView === 1 || t.globalView === 1)
      .sort((a, b) => (a.templateViewName || '').localeCompare(b.templateViewName || ''));
    this.privateTemplates = real
      .filter(t => t.publicView !== 1 && t.globalView !== 1)
      .sort((a, b) => (a.templateViewName || '').localeCompare(b.templateViewName || ''));
  }

  private storeInitialGridState() {
    if (!this.gridApi) return;
    this.initialGridState = {
      columnState: JSON.stringify(this.gridApi.getColumnState()),
      filterState: JSON.stringify(this.gridApi.getFilterModel()),
      groupState: JSON.stringify(this.gridApi.getColumnGroupState()),
      valueState: JSON.stringify(this.gridApi.getValueColumns()?.map(col => col.getColId()) || [])
    };
  }

  resetGridState() {
    if (!this.gridApi) return;

    const valueColumns = this.gridApi.getValueColumns();
    if (valueColumns && valueColumns.length > 0) {
      this.gridApi.removeValueColumns(valueColumns);
    }

    this.gridApi.setFilterModel(null);
    this.gridApi.resetColumnState();
    this.gridApi.resetColumnGroupState();
    
    if (this.pageCode === 'JOBMASTER') {
        this.gridApi.applyColumnState({
          state: [
            { colId: 'jobNumberPenta', sort: 'desc' }
          ],
          applyOrder: true
        });
    }

    this.activeTemplateViewID = 0;
    this.persistActiveViewId(0);
    
    this.saveTemplateState();
    this.storeInitialGridState();
    this.showSaveTemplateButton = false;
    this.showEditTemplateButton = false;
  }

  private setupGridEventListeners() {
    if (!this.gridApi || !this.gridOptions) return;
  
    this.gridApi.addEventListener('sortChanged', () => {
      this.checkGridStateChanges();
    });
  
    this.gridApi.addEventListener('filterChanged', () => {
      this.checkGridStateChanges();
    });
  
    this.gridApi.addEventListener('columnRowGroupChanged', () => {
      this.checkGridStateChanges();
    });
  
    this.gridApi.addEventListener('columnMoved', () => {
      this.checkGridStateChanges();
    });
  
    this.gridApi.addEventListener('columnVisible', () => {
      this.checkGridStateChanges();
    });
  
    this.gridApi.addEventListener('columnPinned', () => {
      this.checkGridStateChanges();
    });
  
    this.gridApi.addEventListener('columnResized', () => {
      this.checkGridStateChanges();
    });
  }

  private checkGridStateChanges() {
    if (!this.gridOptions || !this.gridApi ) return;
  
    const currentState = {
      columnState: JSON.stringify(this.gridApi.getColumnState()),
      filterState: JSON.stringify(this.gridApi.getFilterModel()),
      groupState: JSON.stringify(this.gridApi.getColumnGroupState()),
      valueState: JSON.stringify(this.gridApi.getValueColumns()?.map(col => col.getColId()) || [])
    };
    
    const hasChanges =
      currentState.columnState !== this.initialGridState.columnState ||
      currentState.filterState !== this.initialGridState.filterState ||
      currentState.groupState !== this.initialGridState.groupState ||
      currentState.valueState !== this.initialGridState.valueState;
  
    this.showSaveTemplateButton = hasChanges; 
  }

  saveTemplateChanges() {
    if (this.activeTemplateViewID === 0) {
      this.openTemplateDialog(0);
    } else {
      const currentTemplate = this.templates.find(t => t.templateViewID === this.activeTemplateViewID);
      if (currentTemplate) {
        this.openTemplateDialog(this.activeTemplateViewID);
      }
    }
  }

  getTemplates() {
    this.loadingTemplates = true;
  
    let cached: any = null; 
    
    this.idb.list('gridTemplates').then(result => {
      cached = result;
  
      if (cached && cached.length > 0) {
        const parsedTemplates = cached;
        this.templates = parsedTemplates.filter((t: any) => t.pageCode === this.pageCode);
        this.sortAndGroupTemplates();

        this.idb.select('gridState', this.pageCode).then(state => {
          this.activeTemplateViewID = state ? state.activeTemplateViewID || 0 : 0;
  
          const template = this.templates.find((t: any) => t.templateViewID === this.activeTemplateViewID);
          this.loadingTemplates = false;
  
          if (this.manageViewsEnabled) {
            // Regroup now that the active id is known (the server response may
            // have raced this idb.select and grouped against the default 0)
            this.sortAndGroupTemplates();
          }

          if (this.skipInitialGridReset) {
            this.keepHostRestoredState(template);
          } else if (template && this.activeTemplateViewID !== 0) {
            setTimeout(() => {
              // Re-resolve: the server refresh may have replaced/deleted the
              // row while this apply was pending
              const current = this.templates.find((t: any) => t.templateViewID === template.templateViewID);
              if (current) this.onTemplateSelected(current);
            }, 500);
          } else {
            this.showEditTemplateButton = false;
            this.showSaveTemplateButton = false;
            this.resetGridState();
          }
        });
      }
    }).catch(err => {
      console.error('Error reading templates from IndexedDB', err);
    });
  
    // Flag on: the service owns the v2 command (versioned dispatcher).
    // Legacy path passes nothing and gets the v1 (unchanged) behavior.
    let source$: any;
    if (this.manageViewsEnabled) {
      source$ = this.templateViews.list();
    } else {
      const cmd = new Command();
      cmd.procedure = "cmdTemplateList";
      source$ = this.siteRevService.command(cmd);
    }
    source$.subscribe(retval => {
      const newTemplates = retval.filter((t: any) => t.pageCode === this.pageCode);
  
      this.idb.clearData('gridTemplates').then(() => {
        retval.forEach(template => {
          this.idb.insert('gridTemplates', template);
        });
      });
  
      if (!cached || cached.length === 0) {
        this.templates = newTemplates;
        this.sortAndGroupTemplates();

        this.idb.select('gridState', this.pageCode).then(state => {
          this.activeTemplateViewID = state ? state.activeTemplateViewID || 0 : 0;

          if (this.manageViewsEnabled) {
            // Regroup now that the active id is known (grouping keeps the
            // active view in the dropdown even when unsubscribed)
            this.sortAndGroupTemplates();
          }

          const template = this.templates.find((t: any) => t.templateViewID === this.activeTemplateViewID);
          this.loadingTemplates = false;

          if (this.skipInitialGridReset) {
            this.keepHostRestoredState(template);
          } else if (template && this.activeTemplateViewID !== 0) {
            this.onTemplateSelected(template);
          } else {
            this.showEditTemplateButton = false;
            this.showSaveTemplateButton = false;
            this.resetGridState();
          }
        });
      } else if (this.manageViewsEnabled) {
        // Flag on: the fresh server list always wins (shares/subscriptions
        // change server-side, the stale cache must not mask them)
        const previousActive = this.templates.find((t: any) => t.templateViewID === this.activeTemplateViewID);
        this.templates = newTemplates;
        this.sortAndGroupTemplates();
        const active = newTemplates.find((t: any) => t.templateViewID === this.activeTemplateViewID);
        if (this.activeTemplateViewID !== 0 && !active) {
          this.snackBar.open('The view you were using is no longer available.', 'Close', { duration: 4000 });
          this.resetGridState();
        } else if (active && previousActive &&
                   JSON.stringify(previousActive) !== JSON.stringify(active)) {
          // Same reconciliation the legacy branch does: the active view changed
          // server-side (e.g. an editor updated its layout) — re-apply it
          this.onTemplateSelected(active);
        }
        this.loadingTemplates = false;
      } else {
        const activeTemplate = newTemplates.find((t: any) => t.templateViewID === this.activeTemplateViewID);
        if (activeTemplate && this.activeTemplateViewID !== 0) {
          const currentTemplate = this.templates.find((t: any) => t.templateViewID === this.activeTemplateViewID);
          if (JSON.stringify(currentTemplate) !== JSON.stringify(activeTemplate)) {
            this.templates = newTemplates;
            this.sortAndGroupTemplates();
            this.onTemplateSelected(activeTemplate);
          }
        }
        this.loadingTemplates = false;
      }
    });
  }

  onTemplateChange(event: any) {
    const templateViewID = event.value;
    this.activeTemplateViewID = templateViewID;
    
    this.idb.insert('gridState', {
      pageCode: this.pageCode,
      activeTemplateViewID: templateViewID
    }).catch(() => this.idb.update('gridState', this.pageCode, {
      pageCode: this.pageCode,
      activeTemplateViewID: templateViewID
    }));

    if (templateViewID === 0) {
        this.showEditTemplateButton = false;
        this.showSaveTemplateButton = false;
        this.resetGridState();
    } else {
        const template = this.templates.find(t => t.templateViewID === templateViewID);
        if (template) {
            this.onTemplateSelected(template);
        }
    }
  }

  onTemplateSelected(template: any) {
    this.setTemplateSecurity(template);
    this.activeTemplateViewID = template.templateViewID;
    this.gridState.columnState = JSON.parse(template.columnState);
    this.gridState.filterState = JSON.parse(template.filterState);
    this.gridState.groupState = JSON.parse(template.groupState);
    
    try {
      const sortStateObj = JSON.parse(template.sortState);
      if (sortStateObj && typeof sortStateObj === 'object' && sortStateObj.value) {
        this.gridState.valueState = sortStateObj.value;
        this.gridState.sortState = sortStateObj.sort || []; 
      } else {
        this.gridState.sortState = sortStateObj;
        this.gridState.valueState = null;
      }
    } catch (e) {
      this.gridState.sortState = JSON.parse(template.sortState);
      this.gridState.valueState = null;
    }
    
    this.idb.select('gridState', this.pageCode).then(state => {
      if (state && state.valueState && !this.gridState.valueState) {
        try {
          this.gridState.valueState = JSON.parse(state.valueState);
        } catch (e) {
        }
      }
      this.setTemplateState();
    }).catch(() => {
      this.setTemplateState();
    });
    
    this.showSaveTemplateButton = false;
}

  getTemplateState() {  
    let columnState = [];
    let filterState = {};
    let sortState = [];
    let groupState = [];

    this.idb.select('gridState', this.pageCode).then(state => {
      if (state) {
        this.activeTemplateViewID = state.activeTemplateViewID || 0;
        groupState = JSON.parse(state.groupState || '[]');
        columnState = JSON.parse(state.columnState || '[]');
        filterState = JSON.parse(state.filterState || '{}');
      }
    });

    this.gridState.columnState = columnState;
    this.gridState.filterState = filterState;
    this.gridState.sortState = sortState;
    this.gridState.groupState = groupState;

    if (this.pageCode === 'JOBMASTER' && (this.gridState.columnState.length === 0)) {
      this.gridApi.applyColumnState({
        state: [
          { colId: 'jobNumberPenta', sort: 'desc' }
        ],
        applyOrder: true
      });
    } else {
      this.setTemplateState();
    }
  }

  saveTemplateState() {
    if (!this.gridApi) return;
  
    const groupState = JSON.stringify(this.gridApi.getColumnGroupState());
    const columnState = JSON.stringify(this.gridApi.getColumnState());
    const filterState = JSON.stringify(this.gridApi.getFilterModel());
    const valueState = JSON.stringify(this.gridApi.getValueColumns()?.map(col => col.getColId()) || []);
  
    const templateState = {
      pageCode: this.pageCode,
      columnState,
      groupState,
      filterState,
      valueState,  
      activeTemplateViewID: this.activeTemplateViewID
    };
  
    this.idb.insert('gridState', templateState).catch(() => {
      this.idb.update('gridState', this.pageCode, templateState);
    });
  }

  setTemplateState() {
    if (!this.gridApi) return;
    
    if (this.gridState.groupState && this.gridState.groupState.length > 0) {
      this.gridApi.setColumnGroupState(this.gridState.groupState);
    }
    
    if (this.gridState.columnState && this.gridState.columnState.length > 0) {
      this.gridApi.applyColumnState({ 
        state: this.gridState.columnState, 
        applyOrder: true,
        defaultState: { sort: null }
      });
    }
    
    if (this.gridState.filterState) {
      this.gridApi.setFilterModel(this.gridState.filterState);
    }
    
    setTimeout(() => {
      const allColumns = this.gridApi.getColumns();
      const columnsToAggregate = [];
      
      // 1. Primero intentar usar valueState si existe (de templates guardados localmente)
      if (this.gridState.valueState && Array.isArray(this.gridState.valueState) && this.gridState.valueState.length > 0) {
        const columns = this.gridState.valueState
          .map(colId => this.gridApi.getColumn(colId))
          .filter(col => col != null);
        
        if (columns.length > 0) {
          this.gridApi.addValueColumns(columns);
        }
      } else {
        allColumns?.forEach(column => {
          const colDef = column.getColDef();
          if (colDef.aggFunc && colDef.enableValue) {
            columnsToAggregate.push(column);
          }
        });
        
        if (columnsToAggregate.length > 0) {
          this.gridApi.addValueColumns(columnsToAggregate);
        }
      }
      
      this.gridApi.onFilterChanged();
      this.storeInitialGridState();
      this.setupGridEventListeners();
    }, 200);
  }

  openTemplateDialog(templateViewID?: number) {
    const previousTemplateViewID = this.activeTemplateViewID;

    if (templateViewID === 0) {
      this.activeTemplateViewID = 0;
    }

    const currentTemplate = this.templates.find(t => t.templateViewID === this.activeTemplateViewID);
    const config: MatDialogConfig = {
      data: currentTemplate || { templateViewID: 0 },
      width: '500px',
      height: '300px'
    };

    const dialogRef = this.dialog.open(TemplateDialogComponent, config);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        //console.log("Template saved => ", result);

        let groupState = JSON.stringify(this.gridApi.getColumnGroupState());
        let columnState = JSON.stringify(this.gridApi.getColumnState());
        let filterState = JSON.stringify(this.gridApi.getFilterModel());
        
        const valueColumns = this.gridApi.getValueColumns()?.map(col => col.getColId()) || [];
        const combinedSortState = JSON.stringify({
          sort: JSON.parse(columnState), 
          value: valueColumns  
        });

        let cmd: Command = new Command();
        cmd.procedure = "cmdTemplateViewSave";
        cmd.addParameter("TemplateViewID", result.templateViewID);
        cmd.addParameter("PageCode", this.pageCode);
        cmd.addParameter("TemplateViewName", result.templateViewName);
        cmd.addParameter("groupState", groupState);
        cmd.addParameter("columnState", columnState);
        cmd.addParameter("filterState", filterState);
        cmd.addParameter("sortState", combinedSortState); 
        cmd.addParameter("Public", result.publicView);
        cmd.addParameter("Global", result.globalView);
        cmd.addParameter("Status", result.status);

        this.siteRevService.command(cmd).subscribe(retval => {
          let template = retval[0];
          // v2 proc returns a sentinel row on denial (-2) / bad session (-1);
          // without this guard the code below would persist the bogus id and
          // crash on JSON.parse of the missing state columns.
          if (!template || template.templateViewID < 0) {
            this.snackBar.open("You don't have permission to change this template.", 'Close', { duration: 4000 });
            return;
          }
          let templateID = template.templateViewID;

          this.idb.list('gridTemplates').then(allTemplates => {
            allTemplates = allTemplates || [];

            if (template.status !== "DELETED") {
              this.activeTemplateViewID = templateID;
              
              const valueState = JSON.stringify(valueColumns);
              this.idb.insert('gridState', {
                pageCode: this.pageCode,
                activeTemplateViewID: this.activeTemplateViewID,
                valueState: valueState
              }).catch(() => this.idb.update('gridState', this.pageCode, {
                pageCode: this.pageCode,
                activeTemplateViewID: this.activeTemplateViewID,
                valueState: valueState
              }));

              const index = allTemplates.findIndex(t => t.templateViewID === templateID);
              if (index !== -1) {
                allTemplates[index] = template;
              } else {
                allTemplates.push(template);
              }

              this.idb.clearData('gridTemplates').then(() => {
                allTemplates.forEach(t => this.idb.insert('gridTemplates', t));
              });

              this.onTemplateSelected(template);
            } else if (template.status === "DELETED") {
              this.activeTemplateViewID = 0;
              this.idb.insert('gridState', {
                pageCode: this.pageCode,
                activeTemplateViewID: 0
              }).catch(() => this.idb.update('gridState', this.pageCode, {
                pageCode: this.pageCode,
                activeTemplateViewID: 0
              }));

              allTemplates = allTemplates.filter(t => t.templateViewID !== templateID);

              this.idb.clearData('gridTemplates').then(() => {
                allTemplates.forEach(t => this.idb.insert('gridTemplates', t));
              });
            }

            this.getTemplates();
          });
        });
      } else if (templateViewID === 0) {
        this.activeTemplateViewID = previousTemplateViewID;
        this.idb.insert('gridState', {
          pageCode: this.pageCode,
          activeTemplateViewID: this.activeTemplateViewID
        }).catch(() => {
          this.idb.update('gridState', this.pageCode, {
            pageCode: this.pageCode,
            activeTemplateViewID: this.activeTemplateViewID
          });
        });

        if (this.activeTemplateViewID !== 0) {
          const template = this.templates.find(t => t.templateViewID === this.activeTemplateViewID);
          if (template) {
            this.onTemplateSelected(template);
          } else {
            this.resetGridState();
          }
        } else {
          this.resetGridState();
        }
      }
    });
  }

  // ── Manage Views (MANAGE_VIEWS flag) ──────────────────────────────────────

  /** Persist the active view id for this page (idb.update upserts). */
  private persistActiveViewId(id: number) {
    this.idb.update('gridState', this.pageCode, {
      pageCode: this.pageCode,
      activeTemplateViewID: id
    }).catch(() => { /* cache miss only — server state is unaffected */ });
  }

  // Text shown in the closed views dropdown: the active view's name, or empty
  // when none is active ("Clear View" is a command, not a lingering selection).
  get activeTemplateName(): string {
    if (!this.activeTemplateViewID) return '';
    const t = this.templates.find((x: any) => x.templateViewID === this.activeTemplateViewID);
    return t?.templateViewName || '';
  }

  openManageViews() {
    const config: MatDialogConfig = {
      width: '720px',
      maxHeight: '85vh',
      // The dialog routes backdrop/ESC through its own close() so its result
      // (locally-applied changes) always reaches us.
      disableClose: true,
      data: {
        pageCode: this.pageCode,
        activeTemplateViewID: this.activeTemplateViewID,
        // Instant open: the dialog renders these instead of refetching.
        views: this.templates.map((t: any) => ({ ...t })),
        captureState: () => this.captureGridState(),
        applyView: (view: any) => this.onTemplateSelected(view),
        resetView: () => this.resetGridState(),
      }
    };
    const dialogRef = this.dialog.open(ManageViewsDialogComponent, config);
    dialogRef.afterClosed().subscribe((result?: ManageViewsDialogResult) => {
      if (!result?.dirty) return;   // clean close emits undefined

      // Merge the dialog's authoritative rows locally — no refetch. Every row
      // came from a proc response, so this is server truth for this page.
      if (Array.isArray(result.views)) {
        this.templates = result.views;
        result.views.forEach((t: any) =>
          this.idb.update('gridTemplates', t.templateViewID, t));
        (result.deletedIds || []).forEach(id => this.idb.delete('gridTemplates', id));
      }

      if (result.newActiveId) {
        this.activeTemplateViewID = result.newActiveId;
        this.persistActiveViewId(result.newActiveId);
      }

      // Resolve the active view against the merged rows BEFORE grouping:
      // deleted or unsubscribed-while-active must clear NOW, synchronously —
      // grouping exempts the active view from the subscription filter, so a
      // stale active id would keep a hidden view in the dropdown.
      if (this.activeTemplateViewID !== 0) {
        const active = this.templates.find(t => t.templateViewID === this.activeTemplateViewID);
        if (!active || active.subscribed === 0) {
          this.resetGridState();
        } else if (result.newActiveId) {
          this.onTemplateSelected(active);
        }
      }

      this.sortAndGroupTemplates();
    });
  }

  // Same combined encoding the legacy save flow uses ({ sort, value } in SortState)
  private captureGridState() {
    const groupState = JSON.stringify(this.gridApi.getColumnGroupState());
    const columnState = JSON.stringify(this.gridApi.getColumnState());
    const filterState = JSON.stringify(this.gridApi.getFilterModel());
    const valueColumns = this.gridApi.getValueColumns()?.map(col => col.getColId()) || [];
    const sortState = JSON.stringify({ sort: JSON.parse(columnState), value: valueColumns });
    return { columnState, filterState, sortState, groupState };
  }

  /**
   * skipInitialGridReset path: the host already put the grid back the way the
   * user left it (which includes any active view plus their tweaks on top), so
   * leave the grid alone — only sync the selector's buttons with the active view.
   */
  private keepHostRestoredState(template: any) {
    this.showSaveTemplateButton = false;
    if (template && this.activeTemplateViewID !== 0) {
      this.setTemplateSecurity(template);
    } else {
      this.showEditTemplateButton = false;
    }
    this.storeInitialGridState();
  }

  setTemplateSecurity(template) {
    if (template.createdByID === this.authService.session.internalContactsID) {
      this.showEditTemplateButton = true;
    } else {
      this.showEditTemplateButton = false;
    }
  }

  onSearchByClick() {
    let data = { 
      searchBy: this.searchByOption,
      value: this.searchObj?.input
    };
    this.openSearchDialog(data)
  }

  onSearchByChange(event) { 
    this.searchByOption = event.value;
    let data = { 
      searchBy: this.searchByOption,
      value: ''
    };
    this.openSearchDialog(data);
  }

//   onSearchByPMs() {
//     this.showInputSearch = true;
//     this.searchByOption = 'pMNumber';

//     const config: MatDialogConfig = {
//         width: '35%',
//         height: '40%',
//         data: { searchBy: this.searchByOption, value: this.searchObj?.input }
//     };

//     const dialogRef = this.dialog.open(PgePmSearch, config);

//     dialogRef.afterClosed().subscribe(res => {
//         if (!this.gridApi || !res) {
//             this.showInputSearch = false;
//             return;
//         }
//         this.searchObj = {
//             input: res.searchInput,
//             searchBy: 'pMNumber'
//         };

//         this.bkGridData = this.gridApi.getGridOption('rowData');
//         this.clearFilter = true;              

//         this.gridApi.setGridOption('rowData', []);
//         setTimeout(() => {
//             this.gridApi.setGridOption('rowData', res.data);
//             this.showInputSearch = true;
//         }, 100);
//     });
// }

onSearchByPMs() {
  this.showInputSearch = true;
  this.searchByOption = 'pMNumber';

  const config: MatDialogConfig = {
      width: '35%',
      height: '40%',
      data: { searchBy: this.searchByOption, value: this.searchObj?.input }
  };

  const dialogRef = this.dialog.open(PgePmSearch, config);

  dialogRef.afterClosed().subscribe(res => {
      if (!this.gridApi || !res?.data) {
          this.showInputSearch = false;
          return;
      }

      this.searchObj = {
          input: res.searchInput || '',
          searchBy: 'pMNumber'
      };

      this.bkGridData = this.gridApi.getGridOption('rowData');
      this.clearFilter = true;

      const allResults = res.data || [];
      const found = allResults.filter((item: any) => item.notFound !== 1);
      const notFoundList = allResults.filter((item: any) => item.notFound === 1);

      if (found.length === 0 && notFoundList.length > 0) {
          this.showNoResultsMessage(notFoundList);
          return;
      }

      this.gridApi.setGridOption('rowData', []);
      setTimeout(() => {
          this.gridApi.setGridOption('rowData', found);
          this.showInputSearch = true;
           this.gridApi.onFilterChanged();

          if (notFoundList.length > 0) {
              this.showNotFoundSnackbar(notFoundList);
          }
      }, 100);
  });
}

private showNotFoundSnackbar(notFoundList: any[]) {
  const pmList = notFoundList.map(item => item.requestedJobNumber || item.pMNumber || 'Unknown').join(', ');
  this.snackBar.open(`The following PM#s were not found: ${pmList}`, 'OK', {
      duration: 8000,
      panelClass: ['warning-snackbar']
  });
}

private showNoResultsMessage(notFoundList: any[]) {
  const pmList = notFoundList.map(item => item.requestedJobNumber || item.pMNumber || 'Unknown').join(', ');
  
  this.searchObj.input = `Not found: ${pmList}`;
  this.gridApi.setGridOption('rowData', []);
  this.showInputSearch = true;
   this.gridApi.onFilterChanged();

  this.snackBar.open(`No matching jobs found for: ${pmList}`, 'OK', {
      duration: 10000,
      panelClass: ['error-snackbar']
  });
}

  openSearchDialog(dialogData){
    const config: MatDialogConfig = {
      width: '50%',
      height: '40%',
      data: dialogData
    };
    const dialogRef = this.dialog.open(SearchMultipleDialogComponent, config);
    dialogRef.afterClosed().subscribe(res => {
      //console.log(res);
      if(res)  {
        this.searchObj = {
          input: res,
          searchBy: this.searchByOption
        }
        this.showInputSearch = true;
        this.multipleSearchResult(this.searchObj);
      }
      if(!res) {
        this.showInputSearch = false;
        this.searchByOption = '0';
      }
    });
  }
  
  openWBExpandedSearchDialog(dialogData){
    const config: MatDialogConfig = {
      width: '50%',
      height: '40%',
      data: dialogData
    };
    const dialogRef = this.dialog.open(WbExpandedSearch, config);
    dialogRef.afterClosed().subscribe(res => {
      //console.log(res);
      if (!this.gridApi || !res) return;
      this.bkGridData = this.gridApi.getGridOption('rowData');
      this.clearExpandedSearchBtn = true;
      this.expandedSearchObj = res;
      this.gridApi.setGridOption('rowData', []);
      setTimeout(() => {
        this.gridApi.setGridOption('rowData', res.data);
        this.gridApi.onFilterChanged();
      }, 100);
    });
  }

  clearExpandedSearch() {
    if (!this.gridApi) return;
    this.gridApi.setGridOption('rowData', this.bkGridData);
    this.clearExpandedSearchBtn = false;
  }

  multipleSearchResult(searchObj: any) {
    if (!this.gridApi) return;
  
    const searchBy = searchObj.searchBy;
    const conditions = (searchObj.input || [])
      .map((v: string) => v.trim().toLowerCase())
      .filter((v: string) => v);
  
    this.gridApi.setFilterModel(null);
  
    this.gridApi.setGridOption(
      'isExternalFilterPresent',
      () => conditions.length > 0
    );
  
    this.gridApi.setGridOption(
      'doesExternalFilterPass',
      (node) => {
        if (node.group) return true;
    
        const column = this.gridApi.getColumn(searchBy);
        if (!column) return false;
    
        const rawValue = this.gridApi.getCellValue({
          rowNode: node,
          colKey: column
        });

        //console.log('rawValue', rawValue);
    
        if (rawValue == null) return false;
    
        const value = rawValue.toString().toLowerCase();
        return conditions.some(term => value.includes(term));
      }
    );
  
    this.gridApi.onFilterChanged();
    this.clearFilter = true;
  }
  
  async timeSheetGetReportingWeek(){
    let cmd: Command = new Command();
    cmd.procedure = 'cmdTimeSheetGetReportingWeek';
    
    this.siteRevService.command(cmd).subscribe(res => {
      this.reportWeeks = res;
      this.buildDateOptions();
      this.currentWeek = this.getWeekNumber();
      //console.log("Current Week: ", this.currentWeek);
      const hasInitialWeek = this.initialWeekNumber != null
        && this.reportWeeks.some(weekReport => weekReport.reportingWeekID === this.initialWeekNumber);
      const weekToSelect = hasInitialWeek ? this.initialWeekNumber : this.currentWeek;
      let week = this.reportWeeks.filter(weekReport => weekReport.reportingWeekID === weekToSelect);
      this.weekNumber = week[0].reportingWeekID;
      if(this.showCrewSelector) {
        this.loadForemanList(week[0]);
      } else {
        this.selectedWeek.emit(week[0]);
      }
    });
  }

  selectWeek($event) {
    let ts_selectweek = $event.value
    let week = this.reportWeeks.filter(weekReport => weekReport.reportingWeekID === ts_selectweek );
    this.weekNumber = ts_selectweek;
    if(this.showCrewSelector) {
      this.loadForemanList(week[0]);
    } else {
      this.selectedWeek.emit(week[0]);
    }
  }

  selectDateRange() {
    if (this.startDateRange && this.endDateRange) {
      let startDate = this.startDateRange;
      
      if (this.lockStartDate) {
        const end = new Date(this.endDateRange);
        startDate = new Date(end.getFullYear(), 0, 1);
      }
  
      // const dateRange = {
      //   startDate: startDate.toISOString(),
      //   endDate: this.endDateRange.toISOString()
      // };
      // this.selectedDateRange.emit(dateRange);
      this.selectedDateRange.emit({
      startDate: new Date(this.startDateRange).toLocaleDateString("en-US"),
      endDate: new Date(this.endDateRange).toLocaleDateString("en-US")
    }); 
    }
  }

  setCurrentWeek() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    
    this.startDateRange = new Date(today);
    this.startDateRange.setDate(today.getDate() + diffToMonday);
    this.startDateRange.setHours(0, 0, 0, 0);
    
    this.endDateRange = new Date(this.startDateRange);
    this.endDateRange.setDate(this.startDateRange.getDate() + 6);
    this.endDateRange.setHours(23, 59, 59, 999);
    
    this.selectedDateRange.emit({
      startDate: new Date(this.startDateRange).toLocaleDateString("en-US"),
      endDate: new Date(this.endDateRange).toLocaleDateString("en-US")
    }); 
  } 
    

  getWeekNumber() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    const matchingWeek = this.reportWeeks.find(week => {
      const start = new Date(week.startDate);
      const end = new Date(week.endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
  
      return today >= start && today <= end;
    });
  
    return matchingWeek ? matchingWeek.reportingWeekID : null;
  }

  buildDateOptions() {
    this.dateOptions = this.reportWeeks.map(dateObj => {
      const startDate = new Date(dateObj.startDate);
      const endDate = new Date(dateObj.endDate);
    
      const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
      const endMonth = endDate.toLocaleString('en-US', { month: 'short' });
      const startDay = startDate.getDate();
      const endDay = endDate.getDate();
    
      return {
        ...dateObj,
        text: `${startMonth} ${startDay} - ${endMonth} ${endDay} - ${dateObj.yearNumber}`
      };
    });
  }

  loadForemanList(week){
    let startDate = new Date(week.startDate).toLocaleDateString();
    let endDate = new Date(week.endDate).toLocaleDateString();
    
    this.week = week;

    let cmd: Command = new Command();
    cmd.procedure = 'cmdTimeSheetForemanSelector';
    cmd.addParameter('StartDate', startDate);
    cmd.addParameter('EndDate', endDate);
      this.siteRevService.command(cmd)
        .subscribe(res => {
          this.foreManList = res;
          this.foreManList.sort((a, b) => a.firstName.localeCompare(b.firstName));
          this.employeeID = 0;
          this.selectedWeekWithForeman.emit({week: this.week, foreManID: this.employeeID});
        }, err => {
          alert('An error occured getting the report data, please try again');
          //console.log("cmd error ", err)
        });
  }

  onSelectForeman($event) {
    //console.log('select foreman ', $event, this.week)
    let foreManID = $event.value;
    if (this.week !== undefined && this.week !== null){
      this.selectedWeekWithForeman.emit({week: this.week, foreManID: foreManID});
    }
  }

  /**
   * Apply the search box's current text as the grid's quick filter. Typing
   * already does this on a debounce; this is the explicit trigger for the
   * optional Search button (showSearchButton).
   */
  applySearch() {
    if (!this.searchbox || !this.gridApi) return;

    const searchText = this.searchbox.nativeElement.value;
    this.gridApi.setGridOption('quickFilterText', searchText);
    this.clearFilter = !!searchText;
  }

  onClearFiltersClick() {
    this.searchbox.nativeElement.value = '';
    this.searchByOption = '';
    this.showInputSearch = false;
    this.searchObj = null;
    this.clearFilter = false;
    this.clearExpandedSearchBtn = false;
  
    this.gridApi.setGridOption('quickFilterText', '');
    this.gridApi.setFilterModel(null);
  
    this.gridApi.setGridOption('isExternalFilterPresent', () => false);
    this.gridApi.setGridOption('doesExternalFilterPass', null);

    if (this.bkGridData) {
      this.gridApi.setGridOption('rowData', this.bkGridData);
    }
  
    this.gridApi.onFilterChanged();
  }

  openJobEditHistoryDialog() {
    const spinnerconfig: MatDialogConfig = new MatDialogConfig();
    spinnerconfig.data = {};
    spinnerconfig.width = "450px";
    spinnerconfig.height = "250px";
    spinnerconfig.data.title = 'Loading History Data';
    spinnerconfig.data.comment = 'Please wait...';
    const spinnerDilog = this.dialog.open(SpinnerComponent, spinnerconfig);
    
    var cmd: Command = new Command();
    cmd.procedure = "cmdUserJobHistory"

    this.siteRevService.command(cmd)
      .subscribe(retval => {
        spinnerDilog.close();
        //console.log("res of cmdUserJobHistory => ", retval)
        const config: MatDialogConfig = new MatDialogConfig();
        config.data = retval;
        config.width = "80%";
        config.height = "60%";
        const dialogRef = this.dialog.open(EditHistoryDialogComponent, config);
        dialogRef.afterClosed().subscribe((job: any) => {
          //console.log("job selected to filter => ", job);
          if (job?.jobID != null) {
            let jobInput = job.jobID.toString();
            this.searchbox.nativeElement.value = jobInput;
            this.gridApi.setGridOption('quickFilterText', jobInput);
            this.gridApi.expandAll();
            this.clearFilter = true;
          }
        });
      });
  }

  

  getSort() {
    const colState = this.gridApi.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });
    return sortState;
  }
}

